import amplitudeJS from 'amplitude-js';
import environment from './environment';

/**
 * Core Amplitude analytics functions (non-React)
 * Use this module in non-React contexts like axios interceptors, utilities, etc.
 * For React components, use the useAnalytics hook instead.
 *
 * Pre-init gap: the global error listeners in main.js are registered at module
 * load time, but initialize() runs later (after auth + user-data fetch). Events
 * that arrive in that window are buffered in preInitQueue and replayed by
 * flushPreInitQueue(), which initialize() calls once Amplitude is ready.
 */

const serialize = (value) => {
  if (Array.isArray(value)) {
    if (value.some((val) => Array.isArray(val) || typeof val === 'object')) {
      return value.map(serialize).reduce((acc, v, index) => {
        acc[index] = v;
        return acc;
      }, {});
    }
    return value.map(serialize);
  }
  if (typeof value === 'object' && value !== null) {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = serialize(value[key]);
      return acc;
    }, {});
  }
  return value;
};

export const sanitize = (payload) => {
  const cleanData = {};
  Object.keys(payload).forEach((key) => {
    const value = payload[key];
    if (value !== null && value !== undefined) {
      cleanData[key] = serialize(value);
    }
  });
  return cleanData;
};

const getAmplitudeInstance = () => {
  const currentEnvConfig = environment.get().amplitude;
  return amplitudeJS.getInstance(currentEnvConfig.project);
};

const isInitialized = () => {
  const instance = getAmplitudeInstance();
  return !!(instance.options && instance.options.apiKey);
};

const isDisabled = () => {
  const currentEnvConfig = environment.get().amplitude;
  return currentEnvConfig.disabled;
};

// Buffers calls that arrive before initialize() has run.
const preInitQueue = [];

// Replays all queued events. Called by initialize() once Amplitude is ready.
export const flushPreInitQueue = () => {
  const pending = preInitQueue.splice(0); // snapshot + clear atomically
  pending.forEach(({ fn, args }) => fn(...args));
};

// Discards the queue without replaying — used in tests to prevent cross-test leakage.
export const clearPreInitQueue = () => {
  preInitQueue.splice(0);
};

export const trackError = (error, context = {}) => {
  if (isDisabled()) {
    console.log('[Amplitude Stubbed - Error Occurred]', error, context);
    return;
  }

  if (!isInitialized()) {
    preInitQueue.push({ fn: trackError, args: [error, context] });
    return;
  }

  const instance = getAmplitudeInstance();
  const { type, ...additionalContext } = context;

  const errorPayload = {
    errorType: type || 'Unknown Error',
    errorMessage: error.message || error.statusText || 'Unknown error occurred',
    ...additionalContext,
  };

  if (error.status) {
    errorPayload.statusCode = error.status;
  }
  if (error.config && error.config.url) {
    errorPayload.endpoint = error.config.url;
    errorPayload.method = error.config.method;
  }
  if (error.data) {
    const errorData = error.data.error || error.data.errors || error.data;
    if (errorData && Array.isArray(errorData) && errorData.length > 0) {
      errorPayload.errorCode = errorData[0].code;
      errorPayload.errorDetail = errorData[0].message;
    } else if (errorData && errorData.code) {
      errorPayload.errorCode = errorData.code;
    }
  }

  if (error.stack && !error.status) {
    // 1024 is Amplitude's per-property byte limit for string values.
    errorPayload.stackTrace = error.stack.substring(0, 1024);
  }

  instance.logEvent('Error Occurred', sanitize(errorPayload));
};

export const track = (eventName, payload = {}) => {
  if (isDisabled()) {
    console.log(`[Amplitude Stubbed - ${eventName}]`, payload);
    return;
  }

  if (!isInitialized()) {
    preInitQueue.push({ fn: track, args: [eventName, payload] });
    return;
  }

  const instance = getAmplitudeInstance();
  instance.logEvent(eventName, sanitize(payload));
};

export const identify = (userData) => {
  if (isDisabled()) {
    console.log('[Amplitude Stubbed - setUserProperties]', userData);
    return;
  }

  if (!isInitialized()) {
    preInitQueue.push({ fn: identify, args: [userData] });
    return;
  }

  const instance = getAmplitudeInstance();
  instance.setUserProperties(userData);
};
